import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Building2,
  CalendarDays,
  LayoutDashboard,
  MessageSquare,
  Package,
  Receipt,
  Settings2,
  ShieldCheck,
  Stethoscope,
  Users,
} from 'lucide-react';

export interface NavSubLink {
  to: string;
  label: string;
}

export interface NavItem {
  icon: LucideIcon;
  label: string;
  to: string;
  paths: string[];
  sub?: NavSubLink[];
}

export interface NavSection {
  section: string;
  items: NavItem[];
}

/** Mirrors `templates/nav.js` — route paths use React Router, not `.html` files. */
export const APP_NAV: NavSection[] = [
  {
    section: 'Main',
    items: [
      {
        icon: LayoutDashboard,
        label: 'Dashboard',
        to: '/',
        paths: ['/'],
      },
      {
        icon: Users,
        label: 'Patients',
        to: '/patients',
        paths: ['/patients', '/patients/new', '/patients/recall', '/patients/referrals'],
        sub: [
          { to: '/patients', label: 'Patients List' },
          { to: '/patients/recall', label: 'Recall Management' },
          { to: '/patients/referrals', label: 'Referral Tracker' },
        ],
      },
      {
        icon: CalendarDays,
        label: 'Appointments',
        to: '/appointments/calendar',
        paths: [
          '/appointments/list',
          '/appointments/calendar',
          '/appointments/new',
          '/appointments/waiting-room',
        ],
        sub: [
          { to: '/appointments/calendar', label: 'Calendar' },
          { to: '/appointments/list', label: 'List View' },
          { to: '/appointments/new', label: 'New Appointment' },
          { to: '/appointments/waiting-room', label: 'Waiting Room' },
        ],
      },
      {
        icon: Stethoscope,
        label: 'Clinical',
        to: '/clinical/dental-chart',
        paths: [
          '/clinical',
          '/clinical/consent-forms',
          '/clinical/sterilisation-log',
          '/clinical/lab-orders',
        ],
        sub: [
          { to: '/clinical/consent-forms', label: 'Consent Forms' },
          { to: '/clinical/sterilisation-log', label: 'Sterilisation Log' },
          { to: '/clinical/lab-orders', label: 'Lab Orders' },
        ],
      },
      {
        icon: Receipt,
        label: 'Billing',
        to: '/billing/invoices',
        paths: [
          '/billing',
          '/billing/invoices',
          '/billing/invoices/new',
          '/billing/payments',
          '/billing/insurance-claims',
          '/billing/treatment-estimates',
          '/billing/payment-plans',
          '/billing/credit-notes',
          '/billing/vendor-invoices',
          '/billing/purchase-orders',
        ],
        sub: [
          { to: '/billing/invoices', label: 'Invoices' },
          { to: '/billing/payments', label: 'Payments' },
          { to: '/billing/insurance-claims', label: 'Insurance Claims' },
          { to: '/billing/treatment-estimates', label: 'Treatment Estimates' },
          { to: '/billing/payment-plans', label: 'Payment Plans' },
          { to: '/billing/credit-notes', label: 'Credit Notes' },
          { to: '/billing/vendor-invoices', label: 'Vendor Invoices' },
          { to: '/billing/purchase-orders', label: 'Purchase Orders' },
        ],
      },
    ],
  },
  {
    section: 'Clinic',
    items: [
      {
        icon: MessageSquare,
        label: 'Communication',
        to: '/clinic/messaging',
        paths: [
          '/clinic/messaging',
          '/clinic/notification-templates',
          '/clinic/broadcast-campaigns',
        ],
        sub: [
          { to: '/clinic/messaging', label: 'Messaging Centre' },
          { to: '/clinic/notification-templates', label: 'Notification Templates' },
          { to: '/clinic/broadcast-campaigns', label: 'Broadcast Campaigns' },
        ],
      },
      {
        icon: Building2,
        label: 'Operations',
        to: '/clinic/suppliers',
        paths: ['/clinic/suppliers', '/clinic/assets', '/clinic/waste-disposal'],
        sub: [
          { to: '/clinic/suppliers', label: 'Supplier Directory' },
          { to: '/clinic/assets', label: 'Asset Register' },
          { to: '/clinic/waste-disposal', label: 'Waste Disposal Log' },
        ],
      },
      {
        icon: Package,
        label: 'Inventory',
        to: '/inventory',
        paths: ['/inventory'],
      },
    ],
  },
  {
    section: 'Insights',
    items: [
      {
        icon: BarChart3,
        label: 'Reports',
        to: '/reports',
        paths: ['/reports'],
      },
    ],
  },
  {
    section: 'Admin',
    items: [
      {
        icon: Settings2,
        label: 'Clinic Settings',
        to: '/clinic-settings/profile',
        paths: ['/clinic-settings', '/clinic-settings/profile', '/clinic-settings/clinic-hours', '/clinic-settings/operatories', '/clinic-settings/users', '/clinic-settings/scheduling', '/clinic-settings/notifications', '/clinic-settings/billing', '/clinic-settings/integrations', '/clinic-settings/security'],
      },
      {
        icon: ShieldCheck,
        label: 'Admin',
        to: '/account-settings/general',
        paths: [
          '/account-settings',
          '/account-settings/general',
          '/admin/audit-log',
          '/treatments-catalog',
          '/treatments-catalog/new',
          '/settings/treatment-catalog',
        ],
        sub: [
          { to: '/account-settings/general', label: 'Organization Settings' },
          { to: '/treatments-catalog', label: 'Treatment Catalog' },
          { to: '/admin/audit-log', label: 'Audit Log' },
        ],
      },
    ],
  },
];

const PATIENT_DETAIL_PATTERN = /^\/patients\/(?!new$|medical-history$|communication$|recall$|referrals$)[^/]+(\/[^/]+)?$/;

export function findActiveNavItem(pathname: string): NavItem | null {
  const normalized = pathname.replace(/\/$/, '') || '/';

  for (const group of APP_NAV) {
    for (const item of group.items) {
      if (item.paths.includes(normalized)) return item;
      if (item.paths.some((p) => p !== '/' && normalized.startsWith(`${p}/`))) return item;
    }
  }

  if (PATIENT_DETAIL_PATTERN.test(normalized)) {
    return APP_NAV[0]?.items.find((item) => item.label === 'Patients') ?? null;
  }

  return null;
}
